import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaChevronDown,
  FaChevronUp,
  FaStar,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";

const Surveys = () => {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");

  const [surveys, setSurveys] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState({});
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch surveys
  const fetchSurveys = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/pending-surveys`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const data = response?.data?.data || [];
      setSurveys(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
      } else {
        setError("Failed to load surveys. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, [user?.token, i18n.language]);

  // Auto expand based on ?key= param
  useEffect(() => {
    if (key && surveys.length > 0) {
      const matchingSurvey = surveys.find((s) => s.key === key);
      if (matchingSurvey) setExpanded(matchingSurvey.id);
    }
  }, [key, surveys]);

  const toggleAccordion = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  // Handle answers per survey
  const handleAnswerChange = (surveyId, questionId, value, isMulti = false) => {
    setAnswers((prev) => {
      const surveyAnswers = prev[surveyId] || {};

      if (isMulti) {
        const prevValues = surveyAnswers[questionId] || [];
        return {
          ...prev,
          [surveyId]: {
            ...surveyAnswers,
            [questionId]: prevValues.includes(value)
              ? prevValues.filter((v) => v !== value)
              : [...prevValues, value],
          },
        };
      }

      return {
        ...prev,
        [surveyId]: { ...surveyAnswers, [questionId]: value },
      };
    });
  };

  const handleSubmit = async (survey) => {
    const surveyAnswers = answers[survey.id] || {};

    // Check required fields
    const unanswered = survey.questions?.filter(
      (q) => q.required && (surveyAnswers[q.id] === undefined || surveyAnswers[q.id]?.length === 0)
    );

    if (unanswered.length > 0) {
      setValidationError((prev) => ({
        ...prev,
        [survey.id]: t("PleaseAnswerAllRequiredQuestions"),
      }));
      return;
    }

    setValidationError((prev) => ({
      ...prev,
      [survey.id]: null,
    }));

    const payload = {
      questions: Object.entries(surveyAnswers).map(([id, answer]) => ({
        id,
        answer,
      })),
    };

    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/solving-surveys/${survey.id}`, payload, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);

      setAnswers((prev) => {
        const updated = { ...prev };
        delete updated[survey.id];
        return updated;
      });

      setExpanded(null);
      fetchSurveys();
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionInput = (survey, q) => {
    const value = answers[survey.id]?.[q.id] || "";
    const options = typeof q.options === "string" ? JSON.parse(q.options || "[]") : q.options;

    switch (q.type) {
      case "stars":
        return (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={22}
                className={`cursor-pointer transition ${value >= star ? "text-yellow-400" : "text-gray-300"}`}
                onClick={() => handleAnswerChange(survey.id, q.id, star)}
              />
            ))}
          </div>
        );

      case "multiple_select":
        return (
          <div className="flex flex-col gap-1">
            {options?.map((opt, index) => (
              <label key={index} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(opt.key || opt.value)}
                  onChange={() => handleAnswerChange(survey.id, q.id, opt.key || opt.value, true)}
                />
                <span>{opt.value}</span>
              </label>
            ))}
          </div>
        );

      case "select":
        return (
          <div className="flex flex-col gap-1">
            {options?.map((opt, index) => (
              <label key={index} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`${survey.id}_${q.id}`}
                  value={opt.key || opt.value}
                  checked={value === (opt.key || opt.value)}
                  onChange={() => handleAnswerChange(survey.id, q.id, opt.key || opt.value)}
                />
                <span>{opt.value}</span>
              </label>
            ))}
          </div>
        );

      case "yes_no":
        return (
          <div className="flex gap-4">
            {["Yes", "No"].map((opt) => (
              <label key={opt} className="flex items-center gap-1">
                <input
                  type="radio"
                  name={`${survey.id}_${q.id}_yesno`}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleAnswerChange(survey.id, q.id, e.target.value)}
                />
                {t(opt)}
              </label>
            ))}
          </div>
        );

      case "textarea":
        return (
          <textarea
            rows="3"
            className="w-full rounded-lg border border-gray-300 p-2"
            placeholder={t("EnterYourAnswer")}
            value={value}
            onChange={(e) => handleAnswerChange(survey.id, q.id, e.target.value)}
          />
        );

      default:
        return <p className="italic text-gray-500">{t("UnsupportedQuestionType")}</p>;
    }
  };

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-6 p-4">
        {/* Back button */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-600"
          >
            {i18n.language === "ar" ? <FaChevronCircleRight size={18} /> : <FaChevronCircleLeft size={18} />}
            {t("back")}
          </button>
          <div className="hidden justify-end lg:flex">{/* <LanguageSwitcher defaultTheme /> */}</div>
        </div>

        {/* Accordion */}
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
          </div>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : surveys.length === 0 ? (
          <p className="text-center text-gray-500">{t("NoSurveysFound")}</p>
        ) : (
          <div className="space-y-4">
            {surveys.map((survey) => (
              <div
                key={survey.id}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <button
                  onClick={() => toggleAccordion(survey.id)}
                  className="flex w-full items-center justify-between bg-purple-500 px-4 py-3 text-left text-lg font-semibold text-white"
                >
                  <span>
                    {survey.key === "1"
                      ? t("LoginSurvey")
                      : survey.key === "2"
                        ? i18n.language === "ar"
                          ? `${t("RegistrationSurvey")} - ${survey.session.name} (${survey.session.start_date} ← ${survey.session.end_date})`
                          : `${t("RegistrationSurvey")} - ${survey.session.name} (${survey.session.start_date} → ${survey.session.end_date})`
                        : survey.key === "3"
                          ? `${t("CertificateCompletionSurvey")} - ${survey.course_name}`
                          : survey.key === "4"
                            ? t("PostActivationSurvey")
                            : survey.key}
                  </span>

                  {expanded === survey.id ? (
                    <FaChevronUp className="text-white" />
                  ) : (
                    <FaChevronDown className="text-white" />
                  )}
                </button>

                {expanded === survey.id && (
                  <div className="space-y-4 border-t border-gray-200 bg-gray-50 p-4">
                    {survey.questions?.map((q) => (
                      <div key={q.id} className="rounded-lg bg-white p-3 shadow">
                        <p className="pb-2 font-medium text-gray-800">
                          {q.title} {q.required && <span className="text-red-500">*</span>}
                        </p>
                        {renderQuestionInput(survey, q)}
                      </div>
                    ))}
                    {validationError[survey.id] && (
                      <p className="text-sm text-red-500">{validationError[survey.id]}</p>
                    )}
                    <button
                      disabled={submitting}
                      onClick={() => handleSubmit(survey)}
                      className="mt-3 w-full rounded-lg bg-purple-500 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
                    >
                      {submitting ? t("Submitting") + "..." : t("Submit")}
                    </button>
                    <button onClick={() => navigate("/")} className="w-full text-sm text-gray-800">
                      {t("reminedMeLater")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="animate-scale-in relative flex flex-col items-center rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-green-600">{t("SurveySubmittedSuccessfully")}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Surveys;
